// ─────────────────────────────────────────────────────────────────────────────
// Archivo: reportesRecepcionService.js
// Ubicación sugerida: src/services/reportes/reportesRecepcionService.js
//
// Servicio para los reportes del área de Recepción.
// Cada función llama a un endpoint distinto y devuelve los datos
// ya calculados por el backend.
//
// Endpoints:
//   GET /reportes/recepcion/resumen          → KPIs ejecutivos
//   GET /reportes/recepcion/por-tipo         → Turnos por tipo de trámite
//   GET /reportes/recepcion/por-mes          → Cantidad por mes
//   GET /reportes/recepcion/canal            → Bot vs Presencial
//   GET /reportes/recepcion/por-estado       → Distribución por estado
//   GET /reportes/recepcion/tasa-asistencia  → Tasa de asistencia / ausentismo
//   GET /reportes/recepcion/por-horario      → Horarios con mayor demanda
//
// Uso en el componente:
//
//   import * as reportesRecepcionService from '../../services/reportes/reportesRecepcionService';
//
//   // Carga inicial (todos en paralelo)
//   const data = await reportesRecepcionService.getAllReportes(filtros);
//
//   // O individualmente al cambiar filtros
//   const porEstado = await reportesRecepcionService.getPorEstado(filtros);
// ─────────────────────────────────────────────────────────────────────────────

import apiClient from '../apiClient';

const BASE_URL = '/reportes/recepcion';

// ── Helper: construir params a partir del objeto de filtros ───────────────────
/**
 * @param {Object} filtros
 * @param {string|null} filtros.fechaDesde  — "yyyy-MM-dd" o null
 * @param {string|null} filtros.fechaHasta  — "yyyy-MM-dd" o null
 * @param {string|null} filtros.tipoTramite — filtrar por tipo de trámite o null
 * @param {string|null} filtros.area        — filtrar por área/cola de atención
 * @param {number|null} filtros.referencia  — id de la referencia (canal de origen)
 *        (RTI | LABORAL | TRIBUNALES | ATENCION_PUBLICO | ANSES | COBROS) o null
 * @param {string|null} filtros.canal       — filtrar por canal ("CHATBOT_EXTERNO" | "MANUAL") o null
 * @param {boolean} filtros.incluirPlanilla — suma las atenciones de la planilla manual (mayo a agosto de
 *        2026) en por-tipo, resultado-atencion y motivos-no-avance. Solo lo pide "Estadísticas históricas".
 * @returns {URLSearchParams}
 */
function buildParams(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);
    if (filtros.tipoTramite) params.append('tipoTramite', filtros.tipoTramite);
    if (filtros.area) params.append('area', filtros.area);
    // Se manda el ID y no el nombre: el reporte agrupa por el catálogo (FK), y hay turnos
    // con FK cargada y el texto libre vacío que un filtro por nombre dejaría afuera.
    if (filtros.referencia) params.append('referencia', filtros.referencia);
    if (filtros.canal) params.append('canal', filtros.canal);
    // Tipo de visita (V229): true = solo los que ya eran clientes del estudio, false = solo la gente
    // sin rastro previo. Se compara contra null/undefined y no por truthiness, porque false es un
    // filtro válido y se perdería.
    if (filtros.yaEraCliente !== null && filtros.yaEraCliente !== undefined) {
        params.append('yaEraCliente', filtros.yaEraCliente);
    }
    // La planilla manual no se suma salvo que se pida: el resto de los reportes son solo de turnos del
    // sistema y, si estos cortes la sumaran, los totales de una misma pantalla dejarían de coincidir.
    if (filtros.incluirPlanilla) params.append('incluirPlanilla', 'true');
    return params;
}

// ── 1. Resumen ejecutivo (KPIs del header) ──────────────────────────────────────
/**
 * GET /reportes/recepcion/resumen
 *
 * Devuelve los KPIs principales para los cards del encabezado del componente.
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   totalTurnos: number,
 *   turnosAtendidos: number,
 *   turnosBot: number,
 *   turnosManuales: number,
 *   turnosCancelados: number,
 *   canceladosPorBot: number,
 *   canceladosManual: number,
 *   sobreturnos: number,
 *   tasaAsistencia: number,
 *   tasaAusentismo: number,
 *   tipoMasSolicitado: string|null,
 *   tipoMasSolicitadoCantidad: number|null,
 *   horarioPico: string|null,
 *   horarioPicoCantidad: number|null
 * }>}
 */
export async function getResumen(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/resumen`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 2. Turnos por tipo de trámite ─────────────────────────────────────────────
/**
 * GET /reportes/recepcion/por-tipo
 *
 * Un ítem por cada tipoTramite con cantidad y porcentaje.
 * Ordenado por cantidad DESC.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   tipoTramite: string,
 *   cantidad: number,
 *   porcentaje: number,
 *   atendidos: number,
 *   cancelados: number
 * }>>}
 */
export async function getPorTipo(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-tipo`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 3. Turnos por mes ──────────────────────────────────────────────────────────
/**
 * GET /reportes/recepcion/por-mes
 *
 * Un ítem por cada mes con desglose por canal y por resultado.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   anio: number,
 *   mes: number,
 *   total: number,
 *   bot: number,
 *   manual: number,
 *   atendidos: number,
 *   noAsistio: number,
 *   cancelados: number,
 *   sobreturnos: number,
 *   variacionTotalPct: number|null,
 *   variacionAtendidosPct: number|null
 * }>>}
 */
export async function getPorMes(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-mes`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 4. Bot vs Presencial (canal) ──────────────────────────────────────────────
/**
 * GET /reportes/recepcion/canal
 *
 * Comparación entre turnos originados por chatbot y por recepción manual.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   canal: string,          // "CHATBOT_EXTERNO" | "MANUAL"
 *   canalLabel: string,     // "Bot" | "Presencial"
 *   total: number,
 *   atendidos: number,
 *   noAsistio: number,
 *   cancelados: number,
 *   reprogramados: number,
 *   tasaAsistencia: number,
 *   porcentaje: number
 * }>>}
 */
export async function getPorCanal(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/canal`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 5. Distribución por estado ─────────────────────────────────────────────────
/**
 * GET /reportes/recepcion/por-estado
 *
 * Distribución de turnos por estado actual.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   estado: string,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 */
export async function getPorEstado(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-estado`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 6. Tasa de asistencia por tipo de trámite ─────────────────────────────────
/**
 * GET /reportes/recepcion/tasa-asistencia
 *
 * Para cada tipo de trámite, qué porcentaje de clientes asistió vs no asistió.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   tipoTramite: string,
 *   totalEfectivos: number,
 *   asistieron: number,
 *   noAsistieron: number,
 *   tasaAsistencia: number,
 *   tasaAusentismo: number
 * }>>}
 */
export async function getTasaAsistencia(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/tasa-asistencia`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 7. Demanda por horario ─────────────────────────────────────────────────────
/**
 * GET /reportes/recepcion/por-horario
 *
 * Para cada horario del turnero, cuántos turnos hubo en el período.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   horario: string,    // "HH:mm"
 *   total: number,
 *   bot: number,
 *   manual: number,
 *   atendidos: number,
 *   sobreturnos: number
 * }>>}
 */
export async function getPorHorario(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-horario`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 8. Rendimiento por operador ────────────────────────────────────────────────
/**
 * GET /reportes/recepcion/por-operador
 *
 * Para cada operador: cantidad de turnos finalizados, desglose por tipo de
 * trámite y tiempo promedio de atención (minutos). Turnos sin operador
 * asignado (históricos) llegan agrupados como "Sin operador".
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idOperador: number|null,
 *   operador: string,
 *   totalAtendidos: number,
 *   tiempoPromedioMinutos: number|null,
 *   porTipoTramite: Array<{ tipoTramite: string, cantidad: number }>
 * }>>}
 */
export async function getPorOperador(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-operador`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 9. Mapa de calor de ocupación ──────────────────────────────────────────────
/**
 * GET /reportes/recepcion/heatmap-ocupacion
 *
 * Una celda por día de semana + horario, con la cantidad de turnos.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   diaSemana: number,       // 0=domingo..6=sábado
 *   diaSemanaLabel: string,
 *   horario: string,         // "HH:mm"
 *   total: number
 * }>>}
 */
export async function getHeatmapOcupacion(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/heatmap-ocupacion`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 10. Motivos de no avance ────────────────────────────────────────────────────
/**
 * GET /reportes/recepcion/motivos-no-avance
 *
 * Motivos registrados cuando una atención finaliza sin abrir un trámite.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   motivo: string,
 *   motivoLabel: string,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 */
export async function getMotivosNoAvance(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/motivos-no-avance`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 11. Tiempo de espera en sala ───────────────────────────────────────────────
/**
 * GET /reportes/recepcion/tiempo-espera
 *
 * Tiempo promedio (minutos) entre el horario agendado y el inicio real de la
 * atención, por franja horaria.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   horario: string,   // "HH:mm"
 *   tiempoEsperaPromedioMinutos: number|null
 * }>>}
 */
export async function getTiempoEspera(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/tiempo-espera`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 12. Ausentismo por canal de origen (referencia real) ──────────────────────
/**
 * GET /reportes/recepcion/ausentismo-por-canal
 *
 * Ausentismo agrupado por canal de origen real (catálogo de referencias:
 * Facebook, Instagram, Referido, Google, etc. — ver US-036). Los turnos sin
 * referencia cargada vía catálogo se agrupan como "Sin clasificar".
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idReferencia: number|null,
 *   nombreCanal: string,
 *   total: number,
 *   noAsistio: number,
 *   tasaAusentismo: number,
 *   porcentaje: number
 * }>>}
 */
export async function getAusentismoPorCanal(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/ausentismo-por-canal`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 13. Conversión por tipo de trámite ─────────────────────────────────────────
/**
 * GET /reportes/recepcion/conversion-por-tipo
 *
 * Por cada tipoTramite del turno, cuántos derivaron en la apertura de un trámite
 * (RTI, ART, Jubilación, PUAM, Plan de Pago o Reajuste — ver US-037) vs. cuántos
 * quedaron solo en consulta.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   tipoTramite: string,
 *   totalTurnos: number,
 *   conTramite: number,
 *   tasaConversion: number
 * }>>}
 */
export async function getConversionPorTipo(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/conversion-por-tipo`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 14. Tendencia mensual de conversión / eficiencia ──────────────────────────
/**
 * GET /reportes/recepcion/conversion-mensual
 *
 * Evolución mes a mes de la tasa de conversión (turno → apertura de trámite).
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   anio: number,
 *   mes: number,
 *   totalTurnos: number,
 *   conTramite: number,
 *   tasaConversion: number
 * }>>}
 */
export async function getConversionMensual(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/conversion-mensual`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 15. Tendencia mensual por tipo de trámite, con resultado de la atención ───
/**
 * GET /reportes/recepcion/tendencia-por-tipo
 *
 * Pensada para usarse con filtros.tipoTramite fijo: muestra mes a mes cómo se
 * distribuyó el resultado de la atención para ese trámite puntual.
 *
 * @param {Object} filtros
 * Un item por mes con cada resultado del catálogo que tuvo turnos ese mes ("SIN_CLASIFICAR"
 * = no se cargó el dato). Ver seriesTendenciaPorTipo en pages/Recepcion/reportes/calculosReportes.js.
 *
 * @returns {Promise<Array<{
 *   anio: number,
 *   mes: number,
 *   total: number,
 *   resultados: Array<{resultado: string, resultadoLabel: string, cantidad: number, porcentaje: number}>
 * }>>}
 */
export async function getTendenciaPorTipo(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/tendencia-por-tipo`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 16. Resultado de la atención (4 categorías) ───────────────────────────────
/**
 * GET /reportes/recepcion/resultado-atencion
 *
 * Clasificación de cómo terminó la atención: Inicia ahora / Carpeta a futuro /
 * No viable / Sin clasificar.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   resultado: string,
 *   resultadoLabel: string,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 */
export async function getResultadoAtencion(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/resultado-atencion`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 17. Turnos atendidos por día ──────────────────────────────────────────────
/**
 * GET /reportes/recepcion/por-dia
 *
 * Serie diaria de turnos (a diferencia de por-mes, que agrega por mes completo).
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   fecha: string,   // "yyyy-MM-dd"
 *   total: number,
 *   atendidos: number
 * }>>}
 */
// ── 19. Turnos que se presentaron por día, con su resultado de atención ───────
/**
 * GET /reportes/recepcion/por-dia-resultado
 *
 * La planilla "Turnos atendidos por día": un item por día con turnos presentados y en qué
 * terminó cada atención. Solo trae días con turnos (los días en 0 los completa la pantalla).
 *
 * @returns {Promise<Array<{fecha: string, atendidos: number,
 *   resultados: Array<{resultado: string, resultadoLabel: string, cantidad: number}>}>>}
 */
export async function getPorDiaResultado(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-dia-resultado`, {
        params: buildParams(filtros),
    });
    return Array.isArray(data) ? data : [];
}

export async function getPorDia(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-dia`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 18. Trámites iniciados por tipo de trámite real ───────────────────────────
/**
 * GET /reportes/recepcion/tramites-iniciados-por-tipo
 *
 * Cuenta trámites efectivamente abiertos, agrupados por el tipo REAL del trámite
 * (catálogo tipotramite) — a diferencia de getPorTipo/getConversionPorTipo, que
 * agrupan por el tipoTramite del TURNO (solo 6 valores posibles).
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   nombreTipoTramite: string,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 */
export async function getTramitesIniciadosPorTipo(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/tramites-iniciados-por-tipo`, {
        params: buildParams(filtros),
    });
    return data;
}

/**
 * GET /reportes/recepcion/tramites-abiertos-general
 * No se usa en Recepción (sus estadísticas son de atenciones, 30-sep-2026): queda para la reformulación
 * de "Estadísticas de trámites".
 * Todos los trámites creados en el período, por tipo, separando los abiertos desde una atención de los
 * abiertos por fuera. Solo toma el período: un trámite abierto por fuera no tiene turno del que sacar
 * los demás filtros.
 * @returns {Promise<Array<{nombreTipoTramite: string, total: number, desdeAtencion: number, porFuera: number}>>}
 */
export async function getTramitesAbiertosGeneral(filtros = {}) {
    const params = {};
    if (filtros.fechaDesde) params.fechaDesde = filtros.fechaDesde;
    if (filtros.fechaHasta) params.fechaHasta = filtros.fechaHasta;
    const { data } = await apiClient.get(`${BASE_URL}/tramites-abiertos-general`, { params });
    return data;
}

// ── Helper: llamar todos los endpoints en paralelo ────────────────────────────
/**
 * Llama a los 7 endpoints en paralelo con Promise.all.
 * Recomendado para la carga inicial del componente y cuando cambian los filtros.
 *
 * @param {Object} filtros
 * @param {string|null} filtros.fechaDesde
 * @param {string|null} filtros.fechaHasta
 * @param {string|null} filtros.tipoTramite
 * @param {string|null} filtros.canal
 * @returns {Promise<{
 *   resumen: Object,
 *   porTipo: Array,
 *   porMes: Array,
 *   porCanal: Array,
 *   porEstado: Array,
 *   tasaAsistencia: Array,
 *   porHorario: Array
 * }>}
 */
export async function getAllReportes(filtros = {}) {
    const [resumen, porTipo, porMes, porCanal, porEstado, tasaAsistencia, porHorario] = await Promise.all([
        getResumen(filtros),
        getPorTipo(filtros),
        getPorMes(filtros),
        getPorCanal(filtros),
        getPorEstado(filtros),
        getTasaAsistencia(filtros),
        getPorHorario(filtros),
    ]);
    return { resumen, porTipo, porMes, porCanal, porEstado, tasaAsistencia, porHorario };
}

// ── Helper para obtener solo los reportes principales ─────────────────────────
/**
 * Versión liviana que solo carga los datos principales para la vista inicial.
 * Útil si quieres cargar primero los gráficos principales y luego los detalles.
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   resumen: Object,
 *   porTipo: Array,
 *   porEstado: Array,
 *   porCanal: Array,
 *   porHorario: Array,
 *   porMes: Array,
 *   heatmapOcupacion: Array,
 *   motivosNoAvance: Array,
 *   tiempoEspera: Array
 * }>}
 */
// allSettled en vez de all: si un solo reporte falla (ej. un 500 puntual), el resto igual
// se muestra en vez de dejar toda la pantalla vacía. `huboErrores` le avisa al componente
// para que muestre un aviso sin bloquear los datos que sí llegaron.
export async function getReportesPrincipales(filtros = {}) {
    const resultados = await Promise.allSettled([
        getResumen(filtros),
        getPorTipo(filtros),
        getPorEstado(filtros),
        getPorCanal(filtros),
        getPorHorario(filtros),
        getPorMes(filtros),
        getHeatmapOcupacion(filtros),
        getMotivosNoAvance(filtros),
        getTiempoEspera(filtros),
        getAusentismoPorCanal(filtros),
        getConversionPorTipo(filtros),
        getConversionMensual(filtros),
        getTendenciaPorTipo(filtros),
        getResultadoAtencion(filtros),
        getPorDia(filtros),
        getTramitesIniciadosPorTipo(filtros),
    ]);
    const [resumen, porTipo, porEstado, porCanal, porHorario, porMes, heatmapOcupacion, motivosNoAvance, tiempoEspera, ausentismoPorCanal, conversionPorTipo, conversionMensual, tendenciaPorTipo, resultadoAtencion, porDia, tramitesIniciadosPorTipo] = resultados;

    const fallidos = resultados.filter((r) => r.status === 'rejected');
    if (fallidos.length > 0) {
        console.error(`getReportesPrincipales: ${fallidos.length}/${resultados.length} reportes fallaron`, fallidos.map((r) => r.reason));
    }

    const valorO = (resultado, porDefecto) => (resultado.status === 'fulfilled' ? resultado.value : porDefecto);

    return {
        resumen: valorO(resumen, null),
        porTipo: valorO(porTipo, []),
        porEstado: valorO(porEstado, []),
        porCanal: valorO(porCanal, []),
        porHorario: valorO(porHorario, []),
        porMes: valorO(porMes, []),
        heatmapOcupacion: valorO(heatmapOcupacion, []),
        motivosNoAvance: valorO(motivosNoAvance, []),
        tiempoEspera: valorO(tiempoEspera, []),
        ausentismoPorCanal: valorO(ausentismoPorCanal, []),
        conversionPorTipo: valorO(conversionPorTipo, []),
        conversionMensual: valorO(conversionMensual, []),
        tendenciaPorTipo: valorO(tendenciaPorTipo, []),
        resultadoAtencion: valorO(resultadoAtencion, []),
        porDia: valorO(porDia, []),
        tramitesIniciadosPorTipo: valorO(tramitesIniciadosPorTipo, []),
        huboErrores: fallidos.length > 0,
    };
}